import { useState, useEffect, lazy, Suspense } from 'react';
import { TrackingProvider, useTracking } from '@/components/TrackingProvider';
import { CookieConsentBanner } from '@/components/CookieConsentBanner';

// Lazy load heavy widgets — they're not needed for initial render
const NewsletterPopup = lazy(() => import('@/components/NewsletterPopup').then(m => ({ default: m.NewsletterPopup })));
const ChatWidget = lazy(() => import('@/components/ChatWidget').then(m => ({ default: m.ChatWidget })));

interface MarketingWrapperProps {
    children: React.ReactNode;
}

// Inner component that has access to tracking context
function MarketingContent({ children }: MarketingWrapperProps) {
    const { hasConsent, setConsent } = useTracking();
    const [showConsent, setShowConsent] = useState(false);
    const [cookieBannerVisible, setCookieBannerVisible] = useState(false);

    useEffect(() => {
        // Check if consent was already given
        const consent = localStorage.getItem('noknok_tracking_consent');
        if (consent === null) {
            setShowConsent(true);
        }
    }, []);

    const handleAcceptConsent = () => {
        setConsent(true);
        setShowConsent(false);
    };

    const handleDeclineConsent = () => {
        setConsent(false);
        setShowConsent(false);
        localStorage.setItem('noknok_tracking_consent', 'false');
    };

    return (
        <>
            {children}

            {/* Cookie Consent Banner */}
            {showConsent && (
                <CookieConsentBanner
                    onAccept={handleAcceptConsent}
                    onDecline={handleDeclineConsent}
                    onVisibilityChange={setCookieBannerVisible}
                />
            )}

            {/* Lazy-loaded widgets — won't block initial page render */}
            <Suspense fallback={null}>
                <NewsletterPopup cookieBannerVisible={cookieBannerVisible} />
                {/* Human-first live chat (no AI). Visitor messages route to the
                    dev Live Visitors panel via notify-agents — no platform-funded
                    AI, so no PLATFORM_BUSINESS_ID billing hole. */}
                <ChatWidget />
            </Suspense>
        </>
    );
}

// Outer wrapper that provides tracking context
export function MarketingWrapper({ children }: MarketingWrapperProps) {
    return (
        <TrackingProvider>
            <MarketingContent>{children}</MarketingContent>
        </TrackingProvider>
    );
}
